import { defineStore } from 'pinia';
import { useHubStore } from '~/stores/hub';
import { sessionStill } from '~/util/sessionEpoch';
import { useSettingsStore } from '~/stores/settings';
import { visibleActivity } from '~/util/appCatalog';
import { useOrgKeyUnlockStore } from '~/stores/orgKeyUnlock';
import { decryptDayBundles, decryptRecordsDetailed } from '~/util/deviceActivity';
import { provablyCompleteDays, sealDay } from '~/util/daySealing';
import { dedupeIntervals } from '~/util/intervalPayload';
import { identityPublicKey, parseOrgSecret } from '~/util/orgSigning';
import { secretMatches } from '~/util/keyGrants';
import { pinnedOrgIdentity } from '~/util/pins';
import { publicKeyFromPrivate } from '~/util/orgKeypair';
import { deviceTrust } from '~/util/recordTrust';
/**
 * Each device's verified records as the device produced them, kept so a day
 * can be sealed from the device's own signed records (see daySealing). Held
 * outside the reactive state on purpose: nothing renders from it, and it is
 * cleared with the decrypted records on lock. A device's first load replaces
 * its entry; older history loaded later is added to it.
 */
const signedOriginals = new Map();
/** Bumped whenever the decrypted records are dropped (a lock, a sign-out, a
 * change of client): a fetch or decrypt that began before then finishes into
 * nothing, so it can never put records back, or seal anything, after the key
 * has gone. */
let decryptEpoch = 0;
/** For lists that need no key: the same client, the same person, nothing
 * dropped since. */
function listStillFor(orgId) {
    const epoch = decryptEpoch;
    const session = sessionStill();
    return () => epoch === decryptEpoch && session() && useHubStore().defaultOrganization?.id === orgId;
}
function stillFor(orgId, privateKey) {
    const epoch = decryptEpoch;
    const session = sessionStill();
    return () => epoch === decryptEpoch &&
        session() &&
        useOrgKeyUnlockStore().privateKeyBase64 === privateKey &&
        useHubStore().defaultOrganization?.id === orgId;
}
function remember(deviceId, verified) {
    signedOriginals.set(deviceId, [...(signedOriginals.get(deviceId) ?? []), ...verified]);
}
/** How many of a device's newest summaries the first load fetches (at one
 * summary per 5-minute send, about 41 hours). Older periods are fetched on
 * demand by `ensureHistory`. */
export const RECENT_RECORDS_PER_DEVICE = 500;
/** Page size, and the most pages one `ensureHistory` call fetches per device
 * (20,000 summaries, about 70 days), so a very long range can't run away.
 * Only the stretch sealed days don't cover is paged this way. */
const HISTORY_PAGE_SIZE = 500;
const MAX_HISTORY_PAGES = 40;
/** The most days one day-bundle request spans (the Hub allows `to - from`
 * up to 400). */
const DAY_BUNDLE_CHUNK_DAYS = 400;
const DAY_SECONDS = 86400;
function unixOf(iso) {
    return Math.floor(Date.parse(iso) / 1000);
}
/** The UTC day, `YYYY-MM-DD`, that a unix time falls on. */
export function utcDay(unix) {
    return new Date(unix * 1000).toISOString().slice(0, 10);
}
function dayStartUnix(day) {
    return Date.parse(`${day}T00:00:00Z`) / 1000;
}
/** Every UTC day from `from` to `to`, inclusive. */
function daysBetween(from, to) {
    const days = [];
    for (let t = dayStartUnix(from); t <= dayStartUnix(to); t += DAY_SECONDS) {
        days.push(utcDay(t));
    }
    return days;
}
/** A device's sealed days from `from` to `to`, in as many requests as the
 * Hub's span limit needs. */
async function fetchDayBundles(orgId, deviceId, from, to) {
    const bundles = [];
    for (let start = dayStartUnix(from); start <= dayStartUnix(to);) {
        const end = Math.min(start + DAY_BUNDLE_CHUNK_DAYS * DAY_SECONDS, dayStartUnix(to));
        bundles.push(...(await useHubStore().listDeviceDayBundles(orgId, deviceId, utcDay(start), utcDay(end))));
        start = end + DAY_SECONDS;
    }
    return bundles;
}
/** Summaries received in `[sinceUnix, untilUnix)`, newest first, up to the
 * page cap. `complete` is false when the cap stopped it early. */
async function fetchSummaryRange(orgId, deviceId, sinceUnix, untilUnix) {
    const since = new Date(sinceUnix * 1000).toISOString();
    const until = new Date(untilUnix * 1000).toISOString();
    const summaries = [];
    let after = null;
    for (let page = 0; page < MAX_HISTORY_PAGES; page++) {
        const batch = await useHubStore().listDeviceSummariesInRange(orgId, deviceId, since, until, after);
        summaries.push(...batch);
        if (batch.length < HISTORY_PAGE_SIZE)
            return { summaries, complete: true };
        after = batch[batch.length - 1].id;
    }
    return { summaries, complete: false };
}
/** One device's decrypted records with more decrypted records added. */
function mergeResults(older, existing) {
    return {
        payloads: dedupeIntervals([...existing.payloads, ...older.payloads]),
        failedCount: existing.failedCount + older.failedCount,
        failedKeyVersions: Array.from(new Set([...existing.failedKeyVersions, ...older.failedKeyVersions])).sort((a, b) => a - b),
        unsignedCount: (existing.unsignedCount ?? 0) + (older.unsignedCount ?? 0),
        rejectedCount: (existing.rejectedCount ?? 0) + (older.rejectedCount ?? 0),
        unverifiedCount: (existing.unverifiedCount ?? 0) + (older.unverifiedCount ?? 0),
    };
}
/**
 * Paired devices plus their decrypted activity, shared by every page that
 * views Hub data (Activity, Timeline) so moving between them doesn't
 * re-fetch and re-decrypt the same summaries.
 */
export const useDeviceActivityStore = defineStore('deviceActivity', {
    state: () => ({
        devices: [],
        records: {},
        historyFrom: {},
        loading: false,
        hubIdentity: null,
    }),
    getters: {
        /**
         * The organization identity to check endorsements against: the one in
         * the unlocked key when it carries one (which this browser holds, so the
         * Hub can't substitute it), else the one this browser pinned (from the
         * pins loaded or this browser's sealed copy of them), else what the Hub
         * reports.
         */
        identity(state) {
            const secret = useOrgKeyUnlockStore().privateKeyBase64;
            if (secret) {
                try {
                    const seed = parseOrgSecret(secret).identitySeed;
                    if (seed)
                        return identityPublicKey(seed);
                }
                catch {
                    // Not a readable key; fall back to the pinned identity.
                }
            }
            // The identity this browser pinned for the client (first seen, or
            // verified) before the Hub's word: a Hub that made up a device key and
            // endorsed it with an identity of its own gets no "verified" badge.
            const orgId = useHubStore().defaultOrganization?.id;
            // A browser that has never pinned one (a first visit, with only the
            // encryption half of the key from this tab's storage) checks against
            // what the Hub reports, rather than against nothing: with no identity
            // at all, every signed record would count, a key the Hub made up
            // included.
            return (orgId ? pinnedOrgIdentity(orgId) : null) ?? state.hubIdentity;
        },
        /** Whether the Hub reports a different identity than the unlocked key's:
         * either the wrong key is unlocked, or the Hub isn't telling the truth. */
        identityMismatch(state) {
            return !!state.hubIdentity && !!this.identity && this.identity !== state.hubIdentity;
        },
        /** What to trust for a device's records (see recordTrust). */
        trustFor(state) {
            return (deviceId) => {
                const orgId = useHubStore().defaultOrganization?.id ?? '';
                const device = state.devices.find(d => d.id === deviceId);
                if (!device || !orgId) {
                    return { deviceId, signingKey: null, registeredAt: null, endorsed: false };
                }
                return deviceTrust(device, orgId, this.identity);
            };
        },
        /** Whether any of `deviceIds` still lacks history back to `startUnix`
         * (the per-call page cap was reached, or it hasn't been fetched yet). */
        historyIncomplete(state) {
            return (deviceIds, startUnix) => deviceIds.some(id => {
                const from = state.historyFrom[id];
                return id in state.records && from !== null && from !== undefined && from > startUnix;
            });
        },
        /** Each device's decrypted payloads as the activity views show them:
         * hidden apps left out per the device-activity settings. Recomputed
         * when those settings change, without re-decrypting anything. */
        visible(state) {
            const settings = useSettingsStore();
            const options = {
                hideSystemApps: settings.deviceHideSystemApps,
                hiddenApps: settings.deviceHiddenApps,
            };
            return Object.fromEntries(Object.entries(state.records).map(([id, record]) => [
                id,
                visibleActivity(record.payloads, options),
            ]));
        },
    },
    actions: {
        async loadDevices() {
            const orgId = useHubStore().defaultOrganization?.id;
            if (!orgId)
                return;
            // A list for the client and person that asked, or none: one that comes
            // back after a change of client, a sign-out or a lock is dropped.
            const still = listStillFor(orgId);
            const devices = await useHubStore().listDevices(orgId);
            if (!still())
                return;
            this.devices = devices;
            try {
                const identity = (await useHubStore().getOrgKey(orgId))?.identity_public_key ?? null;
                if (still())
                    this.hubIdentity = identity;
            }
            catch {
                if (still())
                    this.hubIdentity = null;
            }
        },
        /** Re-reads the device list only (connection states change by the
         * second); a failure keeps the list already shown. */
        async refreshDevices() {
            const orgId = useHubStore().defaultOrganization?.id;
            if (!orgId)
                return;
            const still = listStillFor(orgId);
            try {
                const devices = await useHubStore().listDevices(orgId);
                if (still())
                    this.devices = devices;
            }
            catch {
                // The next refresh tries again.
            }
        },
        /** Fetches and decrypts any of `deviceIds` not already cached. */
        async ensureDecrypted(deviceIds) {
            const privateKey = useOrgKeyUnlockStore().privateKeyBase64;
            const orgId = useHubStore().defaultOrganization?.id;
            if (!privateKey || !orgId)
                return;
            const missing = deviceIds.filter(id => !(id in this.records));
            if (missing.length === 0)
                return;
            const still = stillFor(orgId, privateKey);
            this.loading = true;
            try {
                const results = await Promise.all(missing.map(async (id) => {
                    const summaries = await useHubStore().listDeviceSummaries(orgId, id, RECENT_RECORDS_PER_DEVICE);
                    // Fewer than asked for means this is the device's whole history.
                    const from = summaries.length < RECENT_RECORDS_PER_DEVICE
                        ? null
                        : Math.min(...summaries.map(s => unixOf(s.received_at)));
                    if (!still())
                        return null;
                    const { verified, ...result } = await decryptRecordsDetailed(summaries, privateKey, this.trustFor(id));
                    if (!still())
                        return null;
                    // A fresh load: whatever was remembered for the device before is
                    // replaced, not added to.
                    signedOriginals.set(id, verified);
                    return [id, result, from];
                }));
                // Locked, signed out or switched client meanwhile: nothing lands.
                if (!still() || results.some(r => r === null))
                    return;
                const done = results;
                this.records = {
                    ...this.records,
                    ...Object.fromEntries(done.map(([id, result]) => [id, result])),
                };
                this.historyFrom = {
                    ...this.historyFrom,
                    ...Object.fromEntries(done.map(([id, , from]) => [id, from])),
                };
            }
            finally {
                this.loading = false;
            }
        },
        /**
         * Makes sure each loaded device's history reaches back to `startUnix`,
         * fetching only the stretch before what's already loaded. Sealed days
         * come first: one envelope per UTC day holds every interval that started
         * in it. Summaries are paged, newest first, only from the earliest day in
         * that stretch without a readable sealed day, since every day before it
         * is already whole. Devices not loaded yet are left to `ensureDecrypted`.
         *
         * Afterwards, any day in that stretch with no sealed envelope at all,
         * which this browser can now prove complete, is sealed here and sent to
         * the Hub (see `daySealing`), so the next load needn't page it again.
         */
        async ensureHistory(deviceIds, startUnix) {
            const privateKey = useOrgKeyUnlockStore().privateKeyBase64;
            const orgId = useHubStore().defaultOrganization?.id;
            if (!privateKey || !orgId)
                return;
            const needed = deviceIds.filter(id => {
                const from = this.historyFrom[id];
                return id in this.records && from !== null && from !== undefined && from > startUnix;
            });
            if (needed.length === 0)
                return;
            const still = stillFor(orgId, privateKey);
            this.loading = true;
            const unsealed = {};
            try {
                await Promise.all(needed.map(async (id) => {
                    const loadedFrom = this.historyFrom[id];
                    // Summaries received from `loadedFrom` on are loaded, and none
                    // holds activity from after it was received, so the days that
                    // matter run from the period's start to the day `loadedFrom` is in.
                    const days = daysBetween(utcDay(startUnix), utcDay(loadedFrom));
                    const bundles = await fetchDayBundles(orgId, id, days[0], days[days.length - 1]);
                    if (!still())
                        return;
                    const bundled = new Set(bundles.map(b => b.day));
                    unsealed[id] = days.filter(day => !bundled.has(day));
                    const trust = this.trustFor(id);
                    const sealed = await decryptDayBundles(bundles, privateKey, trust);
                    if (!still())
                        return;
                    let merged = mergeResults(sealed, this.records[id]);
                    let reached = startUnix;
                    const firstGap = days.find(day => !sealed.readableDays.has(day));
                    if (firstGap !== undefined) {
                        const since = Math.max(startUnix, dayStartUnix(firstGap));
                        const { summaries, complete } = await fetchSummaryRange(orgId, id, since, loadedFrom);
                        if (!still())
                            return;
                        const { verified, ...older } = await decryptRecordsDetailed(summaries, privateKey, trust);
                        if (!still())
                            return;
                        remember(id, verified);
                        merged = mergeResults(older, merged);
                        // Pages run newest first, so what is loaded always reaches
                        // continuously back from `loadedFrom`; if the page cap stopped
                        // it early, only back to the oldest summary fetched.
                        if (!complete && summaries.length > 0) {
                            reached = Math.min(...summaries.map(s => unixOf(s.received_at)));
                        }
                    }
                    if (!still())
                        return;
                    this.records = { ...this.records, [id]: merged };
                    this.historyFrom = { ...this.historyFrom, [id]: reached };
                }));
            }
            finally {
                this.loading = false;
            }
            if (still())
                await this.sealCompleteDays(orgId, unsealed, still);
        },
        /**
         * Seals and sends each device's days in `unsealedByDevice` that its
         * loaded records prove complete. Best effort, after the page has its
         * data: a day that fails to seal is only slower to load next time, and
         * is tried again then, so failures are dropped rather than shown.
         */
        async sealCompleteDays(orgId, unsealedByDevice, still = () => true) {
            const today = utcDay(Date.now() / 1000);
            const work = Object.entries(unsealedByDevice)
                .map(([id, candidates]) => {
                const records = this.records[id];
                const from = this.historyFrom[id];
                if (!records || from === undefined)
                    return [id, []];
                const days = provablyCompleteDays(candidates.filter(day => day < today), {
                    payloads: records.payloads,
                    failedCount: records.failedCount,
                    loadedFrom: from ?? -Infinity,
                });
                return [id, days];
            })
                .filter(([, days]) => days.length > 0);
            if (work.length === 0)
                return;
            try {
                const key = await useHubStore().getOrgKey(orgId);
                if (!key || !still())
                    return;
                // Sealed only to the key this browser holds, never to whatever the
                // Hub says is current: a Hub that swapped in its own key would
                // otherwise be sent the days' records to read.
                const held = useOrgKeyUnlockStore().privateKeyBase64;
                if (!held || !secretMatches(held, key.public_key))
                    return;
                const toKey = publicKeyFromPrivate(held);
                for (const [id, days] of work) {
                    for (const day of days) {
                        if (!still())
                            return;
                        // Only from the device's own signed records; a day that holds
                        // anything unsigned is left to its summaries.
                        const bundle = await sealDay(day, id, signedOriginals.get(id) ?? [], toKey, key.key_version);
                        if (bundle && still())
                            await useHubStore().sealDeviceDay(orgId, id, bundle);
                    }
                }
            }
            catch {
                // See above: the summaries still hold every one of these days.
            }
        },
        async purgeDevice(deviceId) {
            const orgId = useHubStore().defaultOrganization?.id;
            if (!orgId)
                return;
            await useHubStore().purgeDevice(orgId, deviceId);
            this.devices = this.devices.filter(d => d.id !== deviceId);
            const remaining = { ...this.records };
            delete remaining[deviceId];
            this.records = remaining;
            signedOriginals.delete(deviceId);
        },
        /** Drops every decrypted record. Called on lock. */
        clearDecrypted() {
            decryptEpoch += 1;
            this.records = {};
            this.historyFrom = {};
            signedOriginals.clear();
        },
    },
});
